import React, { useEffect, useState } from "react";
import { searchFilesByModule } from "../../services/FilesService/FilesService";
import SimpleLoader from "../Loader/SimpleLoader";
import VideoCard from "./VideoCard";
import formatGroup from "./formatGroup";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";

// Generic listing page for a video module (YouTube links stored as
// ModuleFiles): group filter + search + card grid.
const VideoModulePage = ({
  moduleCode,
  routeBase,
  title,
  subtitle,
  emptyText,
  defaultLabel,
}) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await searchFilesByModule({ module_code: moduleCode });
        const valid = res?.files?.filter((file) => !!file.link);
        setVideos(valid || []);
      } catch (err) {
        console.error(err);
      } finally {
        setTimeout(() => setLoading(false), 500);
      }
    };

    fetchVideos();
  }, [moduleCode]);

  const uniqueGroups = [
    ...new Set(videos.map((v) => v.group?.toLowerCase()).filter(Boolean)),
  ];

  // Filtered by group and search (empty search matches everything,
  // including label-less videos)
  const filteredVideos = videos.filter((v) => {
    const matchesGroup = filter === "all" || v.group?.toLowerCase() === filter;
    const matchesSearch =
      !searchTerm ||
      (v.label || "").toLowerCase().includes(searchTerm.toLowerCase());
    return matchesGroup && matchesSearch;
  });

  if (loading) {
    return (
      <div className=" bg-gradient-to-br from-purple-50 via-white to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <SimpleLoader className="w-12 h-12 text-[#674da2] mx-auto mt-10 mb-4" />
          <p className="text-gray-600 animate-pulse">Жүктелуде...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold mb-3 bg-[#674da2] bg-clip-text text-transparent">
            {title}
          </h1>
          <p className="text-[#674da2] max-w-2xl mx-auto">{subtitle}</p>
        </div>

        {/* Filter buttons */}
        {uniqueGroups.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <button
              onClick={() => setFilter("all")}
              className={`px-5 py-2.5 rounded-full font-medium transition-all transform hover:scale-105 ${
                filter === "all"
                  ? "text-white bg-[#674da2] shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-50 shadow-md"
              }`}
            >
              Барлығы ({videos.length})
            </button>
            {uniqueGroups.map((group) => {
              const groupData = formatGroup(group);
              const count = videos.filter(
                (v) => v.group?.toLowerCase() === group
              ).length;
              return (
                <button
                  key={group}
                  onClick={() => setFilter(group)}
                  className={`px-5 py-2.5 rounded-full font-medium transition-all transform hover:scale-105 flex items-center gap-2 ${
                    filter === group
                      ? `${groupData.bg} ${groupData.text} shadow-lg`
                      : "bg-white text-gray-700 hover:bg-gray-50 shadow-md"
                  }`}
                >
                  <span>{groupData.icon}</span>
                  <span>
                    {groupData.label} ({count})
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {/* Search Bar */}
        <div className="mb-8 flex justify-center">
          <div className="relative w-full max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Іздеу: видео атауы..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-[#674da2]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Video cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVideos.map((video, index) => (
            <VideoCard
              key={video.uuid}
              video={video}
              index={index}
              routeBase={routeBase}
              defaultLabel={defaultLabel}
            />
          ))}
        </div>

        {/* No videos */}
        {filteredVideos.length === 0 && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📹</div>
            <p className="text-gray-600 text-lg">{emptyText}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoModulePage;
